/**
 * Hard admin session end — single entry (G4).
 * Clear-local-first then best-effort revoke (G1) — not audit-before-clear.
 *
 * Order (G1):
 * 1. markAdminSessionEnding
 * 2. clearLocalSession (React user / isAuthenticated drop — no prolonged auth window)
 * 3. optional postAdminLogoutApi (dual Bearer still in tokenStorage; httpOnly cookies attach)
 * 4. wipe memory + LS
 * 5. broadcast + redirectAfterLogout
 */

import { API_ENDPOINTS } from 'pi-kiosk-shared';
import { isAdminPlatformPwaPath } from '../../app/pwa/adminPwaSurfaces.js';
import { getEnvironmentConfig } from '../../config/environments.js';
import { tokenStorage } from '../../services/storage/tokenStorage.js';
import { stormAwareFetch } from '../http/stormAwareFetch.js';
import { buildTenantApiBase, withTenantInPath } from '../tenant.js';
import { publishAdminAuth } from '../crossTab/adminCrossTab.js';
import { appLogger } from '../logging/appLogger.js';
import { adminAuthCsrfHeaders } from './adminAuthCsrf.js';
import {
  clearAdminSessionEndingLatch,
  markAdminSessionEnding,
} from './adminSessionEndingLatch.js';
import { purgeAdminTokenLocalStorage } from './purgeAdminTokenLocalStorage.js';

export type AdminLogoutSurface = 'platform' | 'tenant';

export interface EndAdminSessionOptions {
  readonly broadcast: boolean;
  readonly callApi: boolean;
  /** platform → POST /api/dev/logout; tenant → POST /api/admin/logout */
  readonly surface: AdminLogoutSurface;
  readonly tenantCode: string;
  /** Clears React/session memory (token state, user, caps, …). */
  readonly clearLocalSession: () => void;
}

/** Coalesce concurrent hard-ends into one in-flight promise. */
let endInFlight: Promise<void> | null = null;

function resolveLogoutPath(surface: AdminLogoutSurface): { path: string; skipTenantPrefix: boolean } {
  if (surface === 'platform') {
    return { path: '/api/dev/logout', skipTenantPrefix: true };
  }
  return { path: API_ENDPOINTS.ADMIN_LOGOUT, skipTenantPrefix: false };
}

/**
 * POST logout best-effort revoke after React clear-local-first.
 * httpOnly cookies still attach via credentials:include; dual Bearer from tokenStorage
 * until wipe. Definitive 4xx still counts as cleared server-side.
 */
export async function postAdminLogoutApi(surface: AdminLogoutSurface): Promise<boolean> {
  const config = getEnvironmentConfig();
  const apiBase = buildTenantApiBase(config.apiUrl);
  const { path, skipTenantPrefix } = resolveLogoutPath(surface);
  const endpoint = skipTenantPrefix ? path : withTenantInPath(path);
  const memoryOrLs = tokenStorage.get();

  try {
    const response = await stormAwareFetch(`${apiBase}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Request-ID': crypto.randomUUID(),
        ...adminAuthCsrfHeaders(),
        ...(memoryOrLs ? { Authorization: `Bearer ${memoryOrLs}` } : {}),
      },
      body: JSON.stringify({}),
    });
    if (
      response.ok ||
      (response.status >= 400 && response.status < 500 && response.status !== 429)
    ) {
      return true;
    }
    appLogger.warn('[auth] logout API failed', { status: response.status, surface });
    return false;
  } catch (error) {
    appLogger.warn('[auth] logout API failed', error);
    return false;
  }
}

/**
 * Hard logout once: clear React → optional revoke API → wipe memory/LS → bus → redirect.
 * Global deps redirectAfterLogout covers H1 from apiClient / SSE / proactive.
 */
export async function endAdminSession(opts: EndAdminSessionOptions): Promise<void> {
  if (endInFlight !== null) {
    return endInFlight;
  }

  endInFlight = (async () => {
    // XT-G7 — latch before any clear so concurrent getToken /me cannot revive.
    markAdminSessionEnding();
    try {
      // G1 — drop React auth immediately before any logout await.
      opts.clearLocalSession();

      if (opts.callApi) {
        await postAdminLogoutApi(opts.surface);
      }

      tokenStorage.clearMemory();
      purgeAdminTokenLocalStorage();

      if (opts.broadcast) {
        // XT-G1 notifyLocal false via publishAdminAuth; XT-G2 scope platform|tenant.
        publishAdminAuth({
          type: 'logout',
          tenantCode: opts.tenantCode,
          scope: opts.surface === 'platform' ? 'platform' : 'tenant',
        });
      }

      globalDeps?.redirectAfterLogout?.();
    } finally {
      clearAdminSessionEndingLatch();
      endInFlight = null;
    }
  })();

  return endInFlight;
}

/** Deps registered by AuthProvider — proactive/periodic hard-end path. */
export interface EndAdminSessionGlobalDeps {
  readonly getSurface: () => AdminLogoutSurface;
  readonly getTenantCode: () => string;
  readonly clearLocalSession: () => void;
  /** G3 — hard navigate to login after H1 from apiClient / SSE / proactive. */
  readonly redirectAfterLogout?: () => void;
}

let globalDeps: EndAdminSessionGlobalDeps | null = null;

/** Prefer AuthProvider-registered surface; fall back to `/dev*` path. */
export function getAdminSessionSurface(): AdminLogoutSurface {
  if (globalDeps !== null) {
    return globalDeps.getSurface();
  }
  if (typeof window !== 'undefined' && isAdminPlatformPwaPath(window.location.pathname)) {
    return 'platform';
  }
  return 'tenant';
}

export function registerEndAdminSessionDeps(deps: EndAdminSessionGlobalDeps): void {
  globalDeps = deps;
}

export function unregisterEndAdminSessionDeps(): void {
  globalDeps = null;
}

/**
 * Hard-end for hooks / shared clients (G14 H1).
 * Soft failures must never call this.
 */
export async function endAdminSessionGlobal(opts: {
  readonly broadcast: boolean;
  readonly callApi: boolean;
  readonly reason?: string;
}): Promise<void> {
  const d = globalDeps;
  await endAdminSession({
    broadcast: opts.broadcast,
    callApi: opts.callApi,
    surface: getAdminSessionSurface(),
    tenantCode: d?.getTenantCode() ?? 'default',
    clearLocalSession: d?.clearLocalSession ?? ((): void => undefined),
  });
}
